import {
  isExcludedBuySource,
  normalizeBuySource,
  normalizeBuySourceHostname,
  normalizeBuySources,
  trustedMerchantDirectUrl,
} from "@/components/buy-sources/normalizeSource";

/**
 * Automatic Storefront Hunter Buy Sources queue.
 * Bounded concurrency. Progressive patch. Cache by walmart_item_id.
 * Does not block catalog discovery or Walmart enrichment.
 */

export const BUY_SOURCES_AUTO_CONCURRENCY = 4;
/** Phase 3: auto-source HTTP batch size (must match server max). */
export const BUY_SOURCES_BATCH_SIZE = 8;
/** Phase 3: concurrent batch HTTP requests (provider still capped by process pool). */
export const BUY_SOURCES_BATCH_MAX_CONCURRENCY = 2;
/**
 * Buy Sources result-set freshness on the client.
 *
 * Matches the backend SFH_BUY_SOURCES_TTL_SECONDS default. A discovered source
 * set (provider discovery + retailer confirmation) is expensive, so it is
 * reused for the whole day and only revalidated lazily for visible rows.
 */
export const BUY_SOURCES_AUTO_TTL_MS = 24 * 60 * 60 * 1000;

/** Chunk an array into fixed-size batches (last may be smaller). */
export function chunkBuySourcesBatch(items, size = BUY_SOURCES_BATCH_SIZE) {
  const list = Array.isArray(items) ? items : [];
  const n = Math.max(1, Number(size) || BUY_SOURCES_BATCH_SIZE);
  const out = [];
  for (let i = 0; i < list.length; i += n) out.push(list.slice(i, i + n));
  return out;
}

/** Must match backend QUERY_STRATEGY — bumps invalidate in-memory cache hits. */
export const BUY_SOURCES_QUERY_STRATEGY = "brand_title_price_v1";

/**
 * FE entry schema for unified Buy Sources rail + clickable anchors.
 * Bumps invalidate stale in-memory hits (e.g. empty Mission from pre-unify FE).
 * v2: consolidate same-merchant Google+image rows onto trusted PDP siblings.
 */
export const BUY_SOURCES_ENTRY_SCHEMA = "unify_rail_v2_merchant_consolidate";

/** Jaccard title overlap required to merge same-domain Buy Source siblings. */
export const BUY_SOURCE_TITLE_JACCARD_MIN = 0.58;
export const BUY_SOURCE_TITLE_SHARED_MIN = 4;

export const BUY_SOURCES_STATUS = {
  IDLE: "idle",
  QUEUED: "queued",
  SEARCHING: "searching",
  COMPLETE: "complete",
  NO_MATCH: "no_match",
  UNRESOLVED: "unresolved",
  ERROR: "error",
};

export function emptyBuySourcesEntry(status = BUY_SOURCES_STATUS.IDLE) {
  return {
    status,
    cheaper: [],
    other: [],
    unresolved: [],
    counts: null,
    sources_found: 0,
    walmart_target_price: null,
    error: null,
    failure_reason: null,
    buy_sources_failure_reason: null,
    diagnostics: null,
    checkedAt: null,
    query_strategy: BUY_SOURCES_QUERY_STRATEGY,
    entry_schema: BUY_SOURCES_ENTRY_SCHEMA,
  };
}

/** Stable identity for unique usable Buy Source references. */
export function buySourceIdentityKey(row) {
  const domain = normalizeBuySourceHostname(row?.source_domain || "");
  const title = String(row?.title || "").trim().toLowerCase();
  const direct = String(row?.direct_url || row?.product_url || "").trim().toLowerCase();
  // Placeholder domain from incomplete fixtures — do not invent collisions.
  if (!domain || (domain === "source" && !title && !direct)) return null;
  if (title) return `${domain}::${title}`;
  if (direct) return `${domain}::url:${direct}`;
  const name = String(row?.source_name || "").trim().toLowerCase();
  if (!name || name === "source") return null;
  const price = row?.display_cost ?? row?.observed_purchase_price ?? row?.price ?? "";
  return `domain:${domain}::${name}::${price}`;
}

/**
 * Prefer trusted direct_url / longer candidate title / image / known price
 * when two rows share the same identity (do not discard a merchant PDP).
 */
export function mergeBestBuySource(existing, incoming) {
  if (!existing) return incoming;
  if (!incoming) return existing;
  const out = { ...existing };
  const aTrusted = trustedMerchantDirectUrl(existing);
  const bTrusted = trustedMerchantDirectUrl(incoming);
  if (!aTrusted && bTrusted) {
    out.direct_url = bTrusted;
    out.product_url = bTrusted;
    out.merchant_offer_url = bTrusted;
    out.source_url = bTrusted;
    out.external_href = bTrusted;
    out.direct_link = true;
  } else if (aTrusted) {
    out.direct_url = aTrusted;
    out.product_url = aTrusted;
    out.merchant_offer_url = aTrusted;
    out.source_url = aTrusted;
    out.external_href = aTrusted;
    out.direct_link = true;
  }
  const aTitle = String(existing.candidate_title || existing.title || "");
  const bTitle = String(incoming.candidate_title || incoming.title || "");
  if (bTitle.length > aTitle.length) {
    out.title = bTitle;
    out.candidate_title = bTitle;
    out.product_title = bTitle;
  }
  if (!out.image_url && incoming.image_url) {
    out.image_url = incoming.image_url;
  }
  if (!out.favicon_url && (incoming.favicon_url || incoming.faviconUrl)) {
    out.favicon_url = incoming.favicon_url || incoming.faviconUrl;
    out.faviconUrl = out.favicon_url;
  }
  // Keep Google reference for provenance when present; never as click target.
  if (!out.reference_url && (incoming.reference_url || incoming.google_offer_url)) {
    const ref = incoming.reference_url || incoming.google_offer_url;
    out.reference_url = ref;
    out.google_offer_url = incoming.google_offer_url || ref;
  }
  for (const key of [
    "display_cost",
    "target_equivalent_cost",
    "observed_purchase_price",
    "price",
  ]) {
    if (
      (out[key] == null || Number.isNaN(out[key])) &&
      typeof incoming[key] === "number" &&
      Number.isFinite(incoming[key])
    ) {
      out[key] = incoming[key];
    }
  }
  return out;
}

const _TITLE_STOP = new Set(["the", "and", "with", "for", "from", "lbs", "lb", "oz"]);

export function buySourceTitleTokens(title) {
  return new Set(
    String(title || "")
      .toLowerCase()
      .replace(/[^a-z0-9\s]+/g, " ")
      .split(/\s+/)
      .filter((t) => t.length > 2 && !_TITLE_STOP.has(t)),
  );
}

/** Jaccard overlap of significant title tokens (0..1). */
export function buySourceTitleOverlap(a, b) {
  const A = buySourceTitleTokens(a);
  const B = buySourceTitleTokens(b);
  if (!A.size || !B.size) return 0;
  let inter = 0;
  for (const t of A) {
    if (B.has(t)) inter += 1;
  }
  const union = A.size + B.size - inter;
  return union ? inter / union : 0;
}

function _rowTitle(row) {
  return String(row?.candidate_title || row?.title || row?.product_title || "");
}

/**
 * Same-domain near-duplicate consolidation.
 * Oxylabs often returns a Google+image seller and a separate merchant PDP row
 * for the same retailer/product. Merge them so image cards become clickable
 * when a trusted direct_url sibling exists (never invent Google click targets).
 */

/** Prices compatible when both present (within 12% or $0.50). Missing price is ok. */
export function buySourcePricesCompatible(a, b) {
  const priceOf = (row) => {
    for (const k of ["display_cost", "observed_purchase_price", "price", "target_equivalent_cost"]) {
      const n = Number(row?.[k]);
      if (Number.isFinite(n) && n > 0) return n;
    }
    return null;
  };
  const pa = priceOf(a);
  const pb = priceOf(b);
  if (pa == null || pb == null) return true;
  const diff = Math.abs(pa - pb);
  return diff <= 0.5 || diff / Math.max(pa, pb) <= 0.12;
}

/** GTIN/UPC/MPN/SKU hard conflict blocks merge; matching ids allow merge. */
export function buySourceIdentityCompatible(a, b) {
  const keys = ["gtin", "upc", "mpn", "sku", "ean"];
  for (const k of keys) {
    const av = String(a?.[k] || "").replace(/\D+/g, "");
    const bv = String(b?.[k] || "").replace(/\D+/g, "");
    if (!av || !bv) continue;
    if (av !== bv) return false;
  }
  return true;
}

export function consolidateBuySourcesSameMerchant(rows) {
  const list = (rows || []).filter(Boolean);
  if (list.length < 2) return list;

  const used = new Set();
  const out = [];

  for (let i = 0; i < list.length; i += 1) {
    if (used.has(i)) continue;
    let best = { ...list[i] };
    const domain = normalizeBuySourceHostname(best.source_domain);
    const title = _rowTitle(best);

    for (let j = i + 1; j < list.length; j += 1) {
      if (used.has(j)) continue;
      const other = list[j];
      const otherDomain = normalizeBuySourceHostname(other.source_domain);
      if (!domain || !otherDomain || domain !== otherDomain) continue;

      const otherTitle = _rowTitle(other);
      const shared = [...buySourceTitleTokens(title)].filter((t) =>
        buySourceTitleTokens(otherTitle).has(t),
      ).length;
      if (shared < BUY_SOURCE_TITLE_SHARED_MIN) continue;
      if (buySourceTitleOverlap(title, otherTitle) < BUY_SOURCE_TITLE_JACCARD_MIN) {
        continue;
      }
      if (!buySourcePricesCompatible(best, other)) continue;
      if (!buySourceIdentityCompatible(best, other)) continue;

      best = mergeBestBuySource(best, other);
      used.add(j);
    }

    const trusted = trustedMerchantDirectUrl(best);
    if (trusted) {
      best = {
        ...best,
        direct_url: trusted,
        product_url: trusted,
        merchant_offer_url: trusted,
        source_url: trusted,
        direct_link: true,
        external_href: trusted,
      };
    } else {
      best = {
        ...best,
        direct_link: false,
        external_href: null,
      };
    }

    const normalized = normalizeBuySource(best);
    out.push(normalized || best);
  }

  return out;
}

/**
 * Single normalized set powering metric + visible Buy Sources rail.
 * Prefers cheaper/other over unresolved when the same identity appears twice.
 * Drops excluded domains and intra/inter-bucket duplicates.
 * When duplicates share an identity, enrich with the best trusted direct_url.
 * Priced sources sorted ascending; unresolved (no trusted price) follow.
 */
export function partitionUsableBuySources(entry) {
  const pricedIn = normalizeBuySources([
    ...(entry?.cheaper || []),
    ...(entry?.other || []),
  ]);
  const reviewIn = normalizeBuySources(entry?.unresolved || []);
  const byKey = new Map();
  const keylessPriced = [];
  const keylessReview = [];

  const ingest = (row, bucket) => {
    if (!row || isExcludedBuySource(row)) return;
    const key = buySourceIdentityKey(row);
    if (key == null) {
      if (bucket === "priced") keylessPriced.push(row);
      else keylessReview.push(row);
      return;
    }
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, { row, bucket });
      return;
    }
    byKey.set(key, {
      row: mergeBestBuySource(existing.row, row),
      // Prefer priced bucket if either occurrence was priced.
      bucket: existing.bucket === "priced" || bucket === "priced" ? "priced" : "review",
    });
  };

  for (const row of pricedIn) ingest(row, "priced");
  for (const row of reviewIn) ingest(row, "review");

  const merged = [];
  for (const { row } of byKey.values()) {
    merged.push(row);
  }
  merged.push(...keylessPriced, ...keylessReview);

  // Cross-bucket: Google+image (priced) + merchant PDP (often unresolved/no price).
  const consolidated = consolidateBuySourcesSameMerchant(merged);

  const priceOf = (row) => {
    const v =
      row?.display_cost ??
      row?.target_equivalent_cost ??
      row?.observed_purchase_price ??
      row?.price;
    return typeof v === "number" && Number.isFinite(v) ? v : Number.POSITIVE_INFINITY;
  };

  const priced = [];
  const review = [];
  for (const row of consolidated) {
    if (priceOf(row) !== Number.POSITIVE_INFINITY) priced.push(row);
    else review.push(row);
  }
  priced.sort((a, b) => priceOf(a) - priceOf(b));

  return { priced, review, all: [...priced, ...review] };
}

/** Flattened unique visible Buy Sources for the single rail (priced then unresolved). */
export function visibleBuySourcesFromEntry(entry) {
  return partitionUsableBuySources(entry).all;
}

/**
 * Usable external Buy Sources count from a cached entry.
 * Equals unique cards in the single Buy Sources rail.
 */
export function countBuySourcesFromEntry(entry) {
  if (!entry) return null;
  return visibleBuySourcesFromEntry(entry).length;
}

export function createBuySourcesCache(ttlMs = BUY_SOURCES_AUTO_TTL_MS) {
  const map = new Map();
  return {
    get(itemId) {
      const key = String(itemId || "");
      if (!key) return null;
      const entry = map.get(key);
      if (!entry) return null;
      if (entry.expiresAt <= Date.now()) {
        map.delete(key);
        return null;
      }
      // Never treat error / transient failure as a successful cache hit.
      if (
        entry.value?.status === BUY_SOURCES_STATUS.ERROR ||
        entry.value?.failure_reason ||
        entry.value?.buy_sources_failure_reason
      ) {
        map.delete(key);
        return null;
      }
      // Old title-only / GTIN-first results must not satisfy brand_title_price_v1.
      if (entry.value?.query_strategy !== BUY_SOURCES_QUERY_STRATEGY) {
        map.delete(key);
        return null;
      }
      // Pre-unify rail / empty stale Mission hits must re-fetch.
      if (entry.value?.entry_schema !== BUY_SOURCES_ENTRY_SCHEMA) {
        map.delete(key);
        return null;
      }
      return entry.value;
    },
    set(itemId, value) {
      const key = String(itemId || "");
      if (!key) return;
      // Never poison the cache with failures — Retry / next page load must re-attempt.
      if (
        !value ||
        value.status === BUY_SOURCES_STATUS.ERROR ||
        value.failure_reason ||
        value.buy_sources_failure_reason
      ) {
        map.delete(key);
        return;
      }
      const stamped =
        value && typeof value === "object"
          ? {
              ...value,
              query_strategy: value.query_strategy || BUY_SOURCES_QUERY_STRATEGY,
              entry_schema: value.entry_schema || BUY_SOURCES_ENTRY_SCHEMA,
            }
          : value;
      map.set(key, { value: stamped, expiresAt: Date.now() + ttlMs });
    },
    delete(itemId) {
      const key = String(itemId || "");
      if (!key) return;
      map.delete(key);
    },
    clear() {
      map.clear();
    },
  };
}

export function productItemId(product) {
  return String(product?.walmart_item_id || product?.walmartItemId || "").trim();
}

/**
 * Classify parsed buy-sources response into card status.
 */
export function classifyBuySourcesResult(parsed) {
  // Provider unavailable is never a no-match: render the neutral error state
  // (and never cache it as a result).
  if (String(parsed?.provider_status || "").toLowerCase() === "unavailable") {
    return BUY_SOURCES_STATUS.ERROR;
  }
  const cheaper = parsed?.cheaper_sources || [];
  const other = parsed?.other_sources || [];
  const unresolved = parsed?.unresolved_sources || [];
  if (cheaper.length + other.length > 0) {
    return BUY_SOURCES_STATUS.COMPLETE;
  }
  if (unresolved.length > 0) {
    return BUY_SOURCES_STATUS.UNRESOLVED;
  }
  return BUY_SOURCES_STATUS.NO_MATCH;
}

export function entryFromParsed(parsed, status) {
  const cheaper = normalizeBuySources(parsed?.cheaper_sources || []);
  const other = normalizeBuySources(parsed?.other_sources || []);
  const unresolved = normalizeBuySources(parsed?.unresolved_sources || []);
  const counts = parsed?.counts && typeof parsed.counts === "object" ? parsed.counts : null;
  const partitioned = partitionUsableBuySources({ cheaper, other, unresolved });
  return {
    status,
    cheaper,
    other,
    unresolved,
    counts,
    // Unique usable references (same set as cards / metric).
    sources_found: partitioned.all.length,
    walmart_target_price: parsed?.walmart_target_price ?? null,
    error: null,
    checkedAt: new Date().toISOString(),
    query_strategy: parsed?.query_strategy || BUY_SOURCES_QUERY_STRATEGY,
    entry_schema: BUY_SOURCES_ENTRY_SCHEMA,
  };
}

/**
 * Regroup cached retailer observations against a new Walmart target price
 * without another Google search.
 */
export function regroupAgainstTarget(entry, walmartTargetPrice) {
  if (!entry) return entry;
  const target =
    typeof walmartTargetPrice === "number" && Number.isFinite(walmartTargetPrice)
      ? walmartTargetPrice
      : null;
  if (target == null) {
    return { ...entry, walmart_target_price: entry.walmart_target_price ?? null };
  }

  const comparable = [...(entry.cheaper || []), ...(entry.other || [])];
  const cheaper = [];
  const other = [];
  for (const src of comparable) {
    const cost =
      typeof src?.display_cost === "number"
        ? src.display_cost
        : typeof src?.target_equivalent_cost === "number"
          ? src.target_equivalent_cost
          : typeof src?.observed_purchase_price === "number"
            ? src.observed_purchase_price
            : null;
    const next = {
      ...src,
      walmart_target_price: target,
      difference_vs_walmart:
        cost != null ? Math.round((cost - target) * 100) / 100 : src.difference_vs_walmart ?? null,
    };
    if (cost != null && cost < target) cheaper.push(next);
    else other.push(next);
  }

  const hasPriced = cheaper.length + other.length > 0;
  const unresolved = entry.unresolved || [];
  let status = entry.status;
  if (
    status === BUY_SOURCES_STATUS.COMPLETE ||
    status === BUY_SOURCES_STATUS.NO_MATCH ||
    status === BUY_SOURCES_STATUS.UNRESOLVED
  ) {
    if (hasPriced) status = BUY_SOURCES_STATUS.COMPLETE;
    else if (unresolved.length > 0) status = BUY_SOURCES_STATUS.UNRESOLVED;
    else status = BUY_SOURCES_STATUS.NO_MATCH;
  }

  return {
    ...entry,
    status,
    cheaper,
    other,
    unresolved,
    sources_found: partitionUsableBuySources({ cheaper, other, unresolved }).all.length,
    walmart_target_price: target,
  };
}

export function createAutoSourceTelemetry() {
  return {
    auto_source_requests_started: 0,
    auto_source_cache_hits: 0,
    auto_source_completed: 0,
    auto_source_no_match: 0,
    auto_source_errors: 0,
    buy_sources_attempted: 0,
    buy_sources_succeeded: 0,
    buy_sources_failed: 0,
    buy_sources_pending: 0,
    products_visible: 0,
    buy_sources_requested: 0,
    buy_sources_completed: 0,
    max_concurrency: BUY_SOURCES_AUTO_CONCURRENCY,
    failure_reasons: {},
  };
}

/** Snapshot helpers for page/batch diagnostics. */
export function summarizeBuySourcesState(stateMap, products) {
  const ids = (products || []).map(productItemId).filter(Boolean);
  let pending = 0;
  let succeeded = 0;
  let failed = 0;
  let searching = 0;
  let queued = 0;
  for (const id of ids) {
    const st = stateMap?.[id]?.status;
    if (st === BUY_SOURCES_STATUS.ERROR) failed += 1;
    else if (
      st === BUY_SOURCES_STATUS.COMPLETE ||
      st === BUY_SOURCES_STATUS.NO_MATCH ||
      st === BUY_SOURCES_STATUS.UNRESOLVED
    ) {
      succeeded += 1;
    } else if (st === BUY_SOURCES_STATUS.SEARCHING) searching += 1;
    else if (st === BUY_SOURCES_STATUS.QUEUED) queued += 1;
    else pending += 1;
  }
  return {
    products_visible: ids.length,
    buy_sources_queued: queued,
    buy_sources_searching: searching,
    buy_sources_succeeded: succeeded,
    buy_sources_failed: failed,
    buy_sources_pending: queued + searching + pending,
  };
}

/**
 * Bounded concurrent queue. Worker receives each item in order.
 * Mutates a local queue copy — safe for simple schedulers.
 * Optional sharedGate: share one concurrency limit across overlapping runs
 * (e.g. page queue + Retry) so Retry cannot exceed the page concurrency cap.
 * Uses a fixed worker pool (not one waiter per item) so Retry is not starved
 * behind dozens of still-queued page IDs.
 */
export async function runWithConcurrency(items, limit, worker, sharedGate = null) {
  const queue = [...(items || [])];
  const n = Math.min(Math.max(1, Number(limit) || 1), Math.max(queue.length, 1));
  const runners = Array.from({ length: n }, async () => {
    while (queue.length) {
      const item = queue.shift();
      if (item == null) break;
      if (sharedGate) {
        await sharedGate.run(() => worker(item));
      } else {
        await worker(item);
      }
    }
  });
  await Promise.all(runners);
}

/**
 * Cross-invocation concurrency gate (page queue + Retry share one limit).
 */
export function createConcurrencyGate(limit) {
  const max = Math.max(1, Number(limit) || 1);
  let active = 0;
  const waiters = [];
  let lastQueueWaitMs = 0;

  const acquire = () => {
    const waitStarted = Date.now();
    return new Promise((resolve) => {
      const grant = () => {
        lastQueueWaitMs = Math.max(0, Date.now() - waitStarted);
        active += 1;
        resolve(lastQueueWaitMs);
      };
      if (active < max) {
        grant();
      } else {
        waiters.push(grant);
      }
    });
  };

  const release = () => {
    active = Math.max(0, active - 1);
    const next = waiters.shift();
    if (next) {
      next();
    }
  };

  return {
    get active() {
      return active;
    },
    get limit() {
      return max;
    },
    /** Milliseconds spent waiting for a free slot on the most recent acquire. */
    get lastQueueWaitMs() {
      return lastQueueWaitMs;
    },
    async run(fn) {
      await acquire();
      try {
        return await fn();
      } finally {
        release();
      }
    },
  };
}

/**
 * Build initial per-item state map for a product page.
 * Applies fresh cache hits immediately; others become queued.
 */
export function productTargetPrice(product) {
  const candidates = [
    product?.buy_box_price,
    product?.buyBoxPrice,
    product?.storefront_price,
    product?.storefrontPrice,
    product?.price,
  ];
  for (const v of candidates) {
    if (typeof v === "number" && Number.isFinite(v) && v > 0) return v;
  }
  return null;
}

export function seedBuySourcesState(products, cache, telemetry, eligibleIds = null) {
  const next = {};
  const eligible = eligibleIds instanceof Set ? eligibleIds : null;
  for (const product of products || []) {
    const id = productItemId(product);
    if (!id) continue;
    const hit = cache?.get?.(id);
    if (hit) {
      if (telemetry) telemetry.auto_source_cache_hits += 1;
      next[id] = regroupAgainstTarget(hit, productTargetPrice(product));
    } else if (eligible && !eligible.has(id)) {
      // Offscreen: not scheduled yet. Neutral IDLE state — never a skeleton,
      // never a provider call until the card approaches the viewport.
      next[id] = emptyBuySourcesEntry(BUY_SOURCES_STATUS.IDLE);
    } else {
      next[id] = emptyBuySourcesEntry(BUY_SOURCES_STATUS.QUEUED);
    }
  }
  return next;
}

/**
 * Deterministic per-product Buy Sources readiness.
 *
 * A product is ready when it has a usable Walmart Item ID and a specific title,
 * independent of unrelated page enrichment (Buy Box, seller count, Monthly
 * Sold, Max Cost, Storefront Price recovery, brand/category). Readiness is per
 * product, so one slow-to-enrich product never blocks the others.
 */
export function buySourceReady(product) {
  if (!product) return false;
  if (!productItemId(product)) return false;
  const title = String(
    product?.title ||
      product?.product_title ||
      product?.productTitle ||
      product?.name ||
      ""
  ).trim();
  return title.length > 0;
}

/**
 * IDs that still need a live search (queued / searching not yet complete).
 */
export function pendingBuySourceIds(products, stateMap, cache) {
  const ids = [];
  for (const product of products || []) {
    const id = productItemId(product);
    if (!id) continue;
    if (cache?.get?.(id)) continue;
    const st = stateMap?.[id]?.status;
    // IDLE means "not scheduled yet" (offscreen). It must never enter the
    // provider queue, so a 40-product page does not fan out to 40 searches.
    if (st === BUY_SOURCES_STATUS.IDLE) continue;
    if (
      st === BUY_SOURCES_STATUS.COMPLETE ||
      st === BUY_SOURCES_STATUS.NO_MATCH ||
      st === BUY_SOURCES_STATUS.UNRESOLVED
    ) {
      continue;
    }
    ids.push(id);
  }
  return [...new Set(ids)];
}
